import { SaveOutlined, SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Drawer, Input, Space, Table, notification } from 'antd';
import { React, useEffect, useMemo, useState } from 'react';
import { useGetMpAttributes } from '../../../../utils/api/mpAttributes/useGetMpAttributes';
import { useSelectMpFields } from '../../../../utils/api/mpCategories/useSelectMpFields';
import { formatAttributeData, getAttributesColumns } from './columns';
import ConfirmAttributesModal from './ConfirmAttributesModal';
import './styled.css';

const AddAttributesDrawer = ({ open, category, mpAttributes, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({
    addedCodes: [],
    removedCodes: [],
  });
  // Initialiser selectedRowKeys avec les IDs des attributs déjà sélectionnés
  const [selectedRowKeys, setSelectedRowKeys] = useState(() => {
    if (!mpAttributes) return [];
    return mpAttributes.map((attr) => attr.id.toString());
  });

  const { selectMpFields, isSelecting } = useSelectMpFields();
  const {
    mpAttributes: allAttributes,
    isLoading,
    isError,
    error,
  } = useGetMpAttributes(
    {
      categories__mp_category__id: category?.id,
    },
    { enabled: !!category?.id }
  );

  const selectedAttributeIds = useMemo(() => {
    if (!mpAttributes) return new Set();
    return new Set(mpAttributes.map((attr) => attr.id));
  }, [mpAttributes]);

  // Calculer si des modifications ont été effectuées
  const hasModifications = useMemo(() => {
    const currentSelectedIds = new Set(selectedRowKeys);
    const initialSelectedIds = selectedAttributeIds;

    // Vérifier si des attributs ont été ajoutés
    const hasAdded = [...currentSelectedIds].some(
      (id) => !initialSelectedIds.has(parseInt(id))
    );

    // Vérifier si des attributs ont été supprimés
    const hasRemoved = [...initialSelectedIds].some(
      (id) => !currentSelectedIds.has(id.toString())
    );

    return hasAdded || hasRemoved;
  }, [selectedRowKeys, selectedAttributeIds]);

  const handleAdd = () => {
    // Calculer les attributs ajoutés et supprimés
    const currentSelectedIds = new Set(selectedRowKeys);
    const initialSelectedIds = selectedAttributeIds;

    // Attributs ajoutés : dans currentSelected mais pas dans initialSelected
    const addedAttributeIds = [...currentSelectedIds].filter(
      (id) => !initialSelectedIds.has(parseInt(id))
    );

    // Attributs supprimés : dans initialSelected mais pas dans currentSelected
    const removedAttributeIds = [...initialSelectedIds].filter(
      (id) => !currentSelectedIds.has(id.toString())
    );

    const addedAttributes = allAttributes.filter((attr) =>
      addedAttributeIds.includes(attr.id.toString())
    );
    const removedAttributes = (mpAttributes || []).filter((attr) =>
      removedAttributeIds.includes(attr.id)
    );

    const addedCodes = addedAttributes.map((attr) => attr.code);
    const removedCodes = removedAttributes.map((attr) => attr.code);

    const hasChanges = addedCodes.length > 0 || removedCodes.length > 0;

    if (!hasChanges) {
      notification.info({
        message: 'Aucune modification',
        description: 'Aucun changement à enregistrer',
        placement: 'bottomRight',
        duration: 3,
      });
      return;
    }

    // Stocker les changements et ouvrir la modal
    setPendingChanges({ addedCodes, removedCodes });
    setIsConfirmModalOpen(true);
  };

  const handleConfirmChanges = async () => {
    const { addedCodes, removedCodes } = pendingChanges;

    try {
      // Effectuer les deux requêtes en parallèle
      const promises = [];

      if (addedCodes.length > 0) {
        promises.push(
          new Promise((resolve, reject) => {
            selectMpFields(
              {
                categoryId: category?.id,
                mpFields: addedCodes,
                selected: true,
              },
              {
                onSuccess: resolve,
                onError: reject,
              }
            );
          })
        );
      }

      if (removedCodes.length > 0) {
        promises.push(
          new Promise((resolve, reject) => {
            selectMpFields(
              {
                categoryId: category?.id,
                mpFields: removedCodes,
                selected: false,
              },
              {
                onSuccess: resolve,
                onError: reject,
              }
            );
          })
        );
      }

      await Promise.all(promises);

      notification.success({
        message: 'Modifications enregistrées',
        description: 'Les attributs ont été mis à jour avec succès',
        placement: 'bottomRight',
        duration: 3,
      });
      setIsConfirmModalOpen(false);
      handleClose();
    } catch (error) {
      notification.error({
        message: 'Erreur',
        description: `Erreur : ${error?.message || 'Impossible de mettre à jour les attributs'}`,
        placement: 'bottomRight',
        duration: 3,
      });
      setIsConfirmModalOpen(false);
    }
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
    setPendingChanges({ addedCodes: [], removedCodes: [] });
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  // Mettre à jour selectedRowKeys quand mpAttributes change
  useEffect(() => {
    if (mpAttributes) {
      setSelectedRowKeys(mpAttributes.map((attr) => attr.id.toString()));
    }
  }, [mpAttributes]);

  const filteredAttributes = useMemo(() => {
    if (!allAttributes || allAttributes.length === 0) return [];
    let attributes = allAttributes;

    if (search.trim()) {
      const query = search.toLowerCase().trim();
      attributes = attributes.filter(
        (attr) =>
          attr.name?.toLowerCase().includes(query) ||
          attr.code?.toLowerCase().includes(query)
      );
    }

    return formatAttributeData(attributes);
  }, [allAttributes, search]);

  const columns = useMemo(() => getAttributesColumns(), []);

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
    preserveSelectedRowKeys: true,
  };

  return (
    <Drawer
      title="Ajouter des attributs marketplace"
      placement="right"
      onClose={handleClose}
      open={open}
      width="calc(100vw - 220px)"
      styles={{
        body: { padding: '24px' },
      }}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleAdd}
          loading={isSelecting}
          disabled={!hasModifications}
        >
          Enregistrer
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {isError && (
              <Alert
                message="Erreur"
                description={
                  error?.message || 'Impossible de récupérer les attributs'
                }
                type="error"
                showIcon
              />
            )}
            <Input
              placeholder="Rechercher un attribut par nom ou code..."
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
            <Table
              dataSource={filteredAttributes}
              columns={columns}
              rowSelection={rowSelection}
              loading={isLoading}
              size="small"
              scroll={{ y: 'calc(100vh - 300px)' }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                size: 'small',
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} attributs`,
              }}
              onChange={(pagination) => {
                setCurrentPage(pagination.current);
                setPageSize(pagination.pageSize);
              }}
            />
          </Space>
        </>
      </Space>
      <ConfirmAttributesModal
        open={isConfirmModalOpen}
        onConfirm={handleConfirmChanges}
        onCancel={handleCancelConfirm}
        addedCount={pendingChanges.addedCodes.length}
        removedCount={pendingChanges.removedCodes.length}
        isLoading={isSelecting}
      />
    </Drawer>
  );
};

export default AddAttributesDrawer;
